import React, { useState, useEffect } from "react";
import {
  FiPlus,
  FiTrash2,
  FiAward,
  FiFileText,
  FiCalendar,
  FiTag,
  FiAlertCircle,
  FiZap,
  FiClock,
  FiGlobe,
} from "react-icons/fi";
import toast from "react-hot-toast";
import { createEvent, updateEvent, getRewardCoins } from "../../../api/eventApi";
import {
  isoToUtcInput,
  utcInputToIso,
  formatUtcDateTimeWithSeconds,
  formatLocalDateTime,
  formatUtcInputDisplay,
  formatUtcInputToLocalDisplay,
  getCurrentUtcInput,
  addDaysToUtcInput,
  calculateDuration,
} from "../../../utils/dateUtils";

const DEFAULT_TERMS_PRESETS = [
  "Users must check in daily to earn points and qualify for leaderboard rewards.",
  "Cheating, botting, or multi-accounting will lead to immediate disqualification.",
  "Leaderboard rankings are finalized at the exact event end timestamp.",
  "Prizes will be distributed to eligible wallet addresses within 7 business days after event completion.",
];

const FALLBACK_COINS = [
  { id: 1, symbol: "USDT", name: "Tether USD" },
  { id: 2, symbol: "BTC", name: "Bitcoin" },
  { id: 3, symbol: "ETH", name: "Ethereum" },
  { id: 4, symbol: "SOL", name: "Solana" },
  { id: 5, symbol: "BNB", name: "BNB" },
];

// Helper to convert ISO string to datetime-local input format in UTC (YYYY-MM-DDTHH:mm)
const toInputDateTime = (isoString) => isoToUtcInput(isoString);


// Helper to convert datetime-local input value to strict UTC ISO string
const toIsoDateTime = (inputValue) => utcInputToIso(inputValue);


// Helper to compute smart next rank suggestion (e.g. 1 -> 2, 2-5 -> 6, 30 -> 31)
const getNextSmartRank = (currentPrizes = []) => {
  if (!currentPrizes || currentPrizes.length === 0) return '1';
  const lastPrize = currentPrizes[currentPrizes.length - 1];
  const lastRange = String(lastPrize?.rankRange || '').trim();
  if (!lastRange) return String(currentPrizes.length + 1);

  // 1. Hyphenated range e.g. "2-5" or "10-30"
  const rangeMatch = lastRange.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rangeMatch) {
    const end = parseInt(rangeMatch[2], 10);
    return String(end + 1);
  }

  // 2. Single number e.g. "30"
  if (/^\d+$/.test(lastRange)) {
    const num = parseInt(lastRange, 10);
    return String(num + 1);
  }

  return String(currentPrizes.length + 1);
};

const EventFormModal = ({ isOpen, onClose, onSuccess, initialEvent = null }) => {
  const isEdit = Boolean(initialEvent && (initialEvent.id || initialEvent.eventId));

  const [formData, setFormData] = useState({
    eventName: "",
    eventId: "",
    status: "running",
    checkInCoin: "",
    referralCoin: "",
    miningCycleCoin: "",
    browserPointsPerZx: 10,
    startDate: "",
    endDate: "",
    description: "",
  });

  const [availableCoins, setAvailableCoins] = useState(FALLBACK_COINS);
  const [terms, setTerms] = useState([]);
  const [newTermInput, setNewTermInput] = useState("");
  const [prizes, setPrizes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [currentUtcTime, setCurrentUtcTime] = useState(new Date());

  // Live ticking UTC clock while modal is open
  useEffect(() => {
    if (!isOpen) return;
    setCurrentUtcTime(new Date());
    const timer = setInterval(() => {
      setCurrentUtcTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  // Quick preset helpers
  const handleSetStartToNow = () => {
    const nowUtc = getCurrentUtcInput();
    setFormData((prev) => {
      let newEnd = prev.endDate;
      if (!newEnd || new Date(`${newEnd}:00.000Z`).getTime() <= new Date(`${nowUtc}:00.000Z`).getTime()) {
        newEnd = addDaysToUtcInput(nowUtc, 7);
      }
      return { ...prev, startDate: nowUtc, endDate: newEnd };
    });
    setValidationErrors((prev) => {
      const copy = { ...prev };
      delete copy.startDate;
      delete copy.endDate;
      return copy;
    });
  };

  const handleSetDurationDays = (days) => {
    const base = formData.startDate || getCurrentUtcInput();
    const newEnd = addDaysToUtcInput(base, days);
    setFormData((prev) => ({
      ...prev,
      startDate: prev.startDate || base,
      endDate: newEnd,
    }));
    setValidationErrors((prev) => {
      const copy = { ...prev };
      delete copy.startDate;
      delete copy.endDate;
      return copy;
    });
  };

  const durationText = calculateDuration(formData.startDate, formData.endDate);

  // Fetch available reward coins on modal open
  useEffect(() => {
    if (isOpen) {
      const fetchCoins = async () => {
        try {
          const res = await getRewardCoins();
          const coinList =
            res?.data?.coins ||
            res?.coins ||
            (Array.isArray(res?.data) ? res.data : null);

          if (Array.isArray(coinList) && coinList.length > 0) {
            setAvailableCoins(coinList);
          }
        } catch (err) {
          console.warn("Failed to fetch reward coins, using default coins list:", err);
        }
      };
      fetchCoins();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (initialEvent) {
        setFormData({
          eventName: initialEvent.eventName || "",
          eventId: initialEvent.eventId || "",
          status: initialEvent.status || "running",
          checkInCoin: initialEvent.checkInCoin ?? initialEvent.checkinCoin ?? initialEvent.checkInPoints ?? initialEvent.coin ?? "",
          referralCoin: initialEvent.referralCoin ?? initialEvent.referralCoins ?? initialEvent.referralPoints ?? initialEvent.referralBonus ?? "",
          miningCycleCoin: initialEvent.miningCycleCoin ?? initialEvent.miningCycleCoins ?? initialEvent.miningCycle ?? initialEvent.miningCyclePoints ?? initialEvent.miningCoin ?? initialEvent.miningBonus ?? "",
          browserPointsPerZx: initialEvent.browserPointsPerZx ?? initialEvent.browserPointsRate ?? 10,
          startDate: toInputDateTime(initialEvent.startDate),
          endDate: toInputDateTime(initialEvent.endDate),
          description: initialEvent.description || "",
        });
        setTerms(
          Array.isArray(initialEvent.termsAndConditions)
            ? [...initialEvent.termsAndConditions]
            : []
        );
        setPrizes(
          Array.isArray(initialEvent.prizes)
            ? initialEvent.prizes.map((p) => {
                const hasCoin =
                  p.hasCoinReward != null
                    ? Boolean(p.hasCoinReward)
                    : Boolean(
                        p.coinSymbol ||
                          (p.rewardUsdt && Number(p.rewardUsdt) > 0) ||
                          (p.coinAmount && Number(p.coinAmount) > 0)
                      );
                const coinSym = p.coinSymbol || "USDT";
                const coinAmt =
                  p.coinAmount != null
                    ? p.coinAmount
                    : p.rewardUsdt != null
                    ? p.rewardUsdt
                    : "";

                return {
                  rankRange: p.rankRange || "",
                  rewardZx: p.rewardZx ?? "",
                  hasCoinReward: hasCoin,
                  coinSymbol: coinSym,
                  coinAmount: coinAmt,
                };
              })
            : []
        );
      } else {
        // Default empty values for Create mode
        setFormData({
          eventName: "",
          eventId: "",
          status: "running",
          checkInCoin: "",
          referralCoin: "",
          miningCycleCoin: "",
          browserPointsPerZx: 10,
          startDate: "",
          endDate: "",
          description: "",
        });
        setTerms([]);
        setPrizes([]);
      }
      setValidationErrors({});
      setNewTermInput("");
    }
  }, [isOpen, initialEvent]);

  if (!isOpen) return null;

  const handleSlugGenerate = () => {
    if (!formData.eventName) return;
    const slug = `evt_${formData.eventName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")}`;
    setFormData((prev) => ({ ...prev, eventId: slug }));
  };

  // Terms handlers
  const handleAddTerm = () => {
    if (!newTermInput.trim()) return;
    setTerms((prev) => [...prev, newTermInput.trim()]);
    setNewTermInput("");
  };

  const handleRemoveTerm = (index) => {
    setTerms((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddPresetTerm = (preset) => {
    if (!terms.includes(preset)) {
      setTerms((prev) => [...prev, preset]);
    }
  };

  // Prizes handlers
  const handleAddPrizeTier = () => {
    const defaultSymbol = availableCoins[0]?.symbol || "USDT";
    setPrizes((prev) => {
      const nextRank = getNextSmartRank(prev);
      return [
        ...prev,
        {
          rankRange: nextRank,
          rewardZx: "",
          hasCoinReward: false,
          coinSymbol: defaultSymbol,
          coinAmount: "",
        },
      ];
    });
  };

  const handlePrizeChange = (index, field, value) => {
    setPrizes((prev) => {
      const updated = [...prev];
      if (field === "hasCoinReward") {
        const isChecked = Boolean(value);
        const defaultSymbol =
          updated[index].coinSymbol || availableCoins[0]?.symbol || "USDT";
        updated[index] = {
          ...updated[index],
          hasCoinReward: isChecked,
          coinSymbol: isChecked ? defaultSymbol : null,
          coinAmount: isChecked ? (updated[index].coinAmount || 0) : 0,
        };
      } else if (field === "rankRange" || field === "coinSymbol") {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      } else {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      }
      return updated;
    });
  };

  const handleRemovePrizeTier = (index) => {
    setPrizes((prev) => prev.filter((_, i) => i !== index));
  };

  // Calculation totals
  const totalZx = prizes.reduce((acc, curr) => acc + (Number(curr.rewardZx) || 0), 0);
  const coinTotals = prizes.reduce((acc, curr) => {
    if (curr.hasCoinReward && curr.coinSymbol && Number(curr.coinAmount) > 0) {
      const sym = curr.coinSymbol;
      acc[sym] = (acc[sym] || 0) + Number(curr.coinAmount);
    }
    return acc;
  }, {});

  const validate = () => {
    const errors = {};
    if (!formData.eventName.trim()) {
      errors.eventName = "Event name is required";
    }
    if (!formData.startDate) {
      errors.startDate = "Start date is required";
    }
    if (!formData.endDate) {
      errors.endDate = "End date is required";
    }
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate).getTime();
      const end = new Date(formData.endDate).getTime();
      if (end <= start) {
        errors.endDate = "End date must be strictly after Start date";
      }
    }
    if (
      formData.browserPointsPerZx !== "" &&
      formData.browserPointsPerZx !== null &&
      (isNaN(Number(formData.browserPointsPerZx)) || Number(formData.browserPointsPerZx) < 0)
    ) {
      errors.browserPointsPerZx = "Browser Points rate must be a number >= 0";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix form validation errors");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        eventName: formData.eventName.trim(),
        startDate: toIsoDateTime(formData.startDate),
        endDate: toIsoDateTime(formData.endDate),
        status: formData.status,
        checkInCoin: Number(formData.checkInCoin) || 0,
        checkinCoin: Number(formData.checkInCoin) || 0,
        checkInPoints: Number(formData.checkInCoin) || 0,
        referralCoin: Number(formData.referralCoin) || 0,
        referralCoins: Number(formData.referralCoin) || 0,
        referralPoints: Number(formData.referralCoin) || 0,
        miningCycleCoin: Number(formData.miningCycleCoin) || 0,
        miningCycleCoins: Number(formData.miningCycleCoin) || 0,
        miningCycle: Number(formData.miningCycleCoin) || 0,
        miningCyclePoints: Number(formData.miningCycleCoin) || 0,
        miningCoin: Number(formData.miningCycleCoin) || 0,
        browserPointsPerZx: (formData.browserPointsPerZx !== "" && formData.browserPointsPerZx != null && !isNaN(Number(formData.browserPointsPerZx)) && Number(formData.browserPointsPerZx) >= 0) ? Number(formData.browserPointsPerZx) : 10,
        browserPointsRate: (formData.browserPointsPerZx !== "" && formData.browserPointsPerZx != null && !isNaN(Number(formData.browserPointsPerZx)) && Number(formData.browserPointsPerZx) >= 0) ? Number(formData.browserPointsPerZx) : 10,
        description: formData.description.trim(),
        termsAndConditions: terms,
        prizes: prizes.map((p) => {
          const hasCoin = Boolean(p.hasCoinReward);
          const coinSym = hasCoin ? (p.coinSymbol || "USDT") : null;
          const coinAmt = hasCoin ? (Number(p.coinAmount) || 0) : 0;
          return {
            rankRange: String(p.rankRange).trim(),
            rewardZx: Number(p.rewardZx) || 0,
            hasCoinReward: hasCoin,
            coinSymbol: coinSym,
            coinAmount: coinAmt,
          };
        }),
      };

      if (formData.eventId && formData.eventId.trim()) {
        payload.eventId = formData.eventId.trim();
      }

      if (isEdit) {
        const idToUpdate = initialEvent.id || initialEvent.eventId;
        await updateEvent(idToUpdate, payload);
        toast.success("Event updated successfully!");
      } else {
        await createEvent(payload);
        toast.success("Event created successfully!");
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save event");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8 overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#ffffcc] text-[#999900] flex items-center justify-center font-bold">
              <FiCalendar size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                {isEdit ? "Edit Event" : "Create New Event"}
              </h3>
              <p className="text-xs text-gray-400">
                {isEdit
                  ? `Updating details for ${initialEvent.eventName || initialEvent.eventId}`
                  : "Configure event dates, dynamic coin prizes, and guidelines"}
              </p>
            </div>
          </div>
          {/* Close button removed as requested */}
        </div>

        {/* Form Body - Scrollable */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Section 1: Basic Details */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FiTag /> Basic Event Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Event Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Season 1 Championship"
                  value={formData.eventName}
                  onChange={(e) => setFormData({ ...formData, eventName: e.target.value })}
                  className={`w-full px-3.5 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] ${
                    validationErrors.eventName ? "border-red-400 bg-red-50/30" : "border-gray-200"
                  }`}
                  required
                />
                {validationErrors.eventName && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertCircle size={12} /> {validationErrors.eventName}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Event Slug / Identifier
                  </label>
                  {!isEdit && (
                    <button
                      type="button"
                      onClick={handleSlugGenerate}
                      className="text-[11px] text-[#999900] hover:underline font-medium"
                    >
                      Generate Slug
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="e.g. evt_season_1 (Auto-generated if blank)"
                  value={formData.eventId}
                  onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
                  disabled={isEdit}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] font-mono text-xs disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">
                  {isEdit ? "Event identifier cannot be changed." : "Optional unique identifier."}
                </span>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white"
                >
                  <option value="running">🟢 Running (Active)</option>
                  <option value="upcoming">🟡 Upcoming (Scheduled)</option>
                  <option value="completed">🔵 Completed</option>
                  <option value="noEvent">⚪ No Event (Inactive / Draft)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Check-in Coin <span className="text-gray-400 font-normal">(Coins per check-in)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🪙</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 10"
                    value={formData.checkInCoin}
                    onChange={(e) => setFormData({ ...formData, checkInCoin: e.target.value })}
                    className="w-full pl-9 pr-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Referral Coin <span className="text-gray-400 font-normal">(Coins per referral)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">👥</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 50"
                    value={formData.referralCoin}
                    onChange={(e) => setFormData({ ...formData, referralCoin: e.target.value })}
                    className="w-full pl-9 pr-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Mining Cycle Coin <span className="text-gray-400 font-normal">(Coins per mining cycle completed)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">⚡</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 20"
                    value={formData.miningCycleCoin}
                    onChange={(e) => setFormData({ ...formData, miningCycleCoin: e.target.value })}
                    className="w-full pl-9 pr-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Zeta Browser Points per ZX <span className="text-gray-400 font-normal">(1 ZX = X Points)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🌐</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 10 (1 ZX = 10 Event Points)"
                    value={formData.browserPointsPerZx}
                    onChange={(e) => setFormData({ ...formData, browserPointsPerZx: e.target.value })}
                    className={`w-full pl-9 pr-3.5 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] ${
                      validationErrors.browserPointsPerZx ? "border-red-400 bg-red-50/30" : "border-gray-200"
                    }`}
                  />
                </div>
                <span className="text-[11px] text-gray-400 mt-1 block">
                  Number of Event Points awarded for every 1 ZX earned via Zeta Browser during this event.
                </span>
                {validationErrors.browserPointsPerZx && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertCircle size={12} /> {validationErrors.browserPointsPerZx}
                  </p>
                )}
              </div>

              {/* UTC Live Clock & Timezone Notice Banner */}
              <div className="sm:col-span-2 bg-gradient-to-r from-amber-50/90 via-[#ffffcc]/50 to-yellow-50/90 border border-amber-200 rounded-xl p-3.5 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-start md:items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-100 text-[#999900] flex items-center justify-center flex-shrink-0">
                      <FiClock size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-[#ffff00] text-black tracking-wide border border-yellow-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          LIVE UTC CLOCK
                        </span>
                        <span className="text-sm font-mono font-bold text-gray-900">
                          {formatUtcDateTimeWithSeconds(currentUtcTime)}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                        <FiGlobe size={12} className="text-gray-400 flex-shrink-0" />
                        <span>
                          Your Local Time: <strong className="font-semibold text-gray-700">{formatLocalDateTime(currentUtcTime, { showSeconds: true })}</strong>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Preset Buttons */}
                  <div className="flex flex-wrap items-center gap-1.5 self-start md:self-center">
                    <span className="text-[10px] uppercase font-bold text-gray-400 mr-0.5">Quick Set:</span>
                    <button
                      type="button"
                      onClick={handleSetStartToNow}
                      className="px-2.5 py-1 bg-white hover:bg-yellow-50 text-gray-800 hover:text-black border border-gray-200 hover:border-yellow-400 rounded-lg text-xs font-semibold transition shadow-xs flex items-center gap-1"
                      title="Set Start Date to current UTC timestamp"
                    >
                      ⚡ Start Now (UTC)
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetDurationDays(7)}
                      className="px-2 py-1 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg text-xs font-medium transition"
                      title="Set End Date to 7 days from start"
                    >
                      +7 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetDurationDays(14)}
                      className="px-2 py-1 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg text-xs font-medium transition"
                      title="Set End Date to 14 days from start"
                    >
                      +14 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetDurationDays(30)}
                      className="px-2 py-1 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg text-xs font-medium transition"
                      title="Set End Date to 30 days from start"
                    >
                      +30 Days
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Start Date & Time <span className="text-red-500">*</span>
                  <span className="ml-1 text-[10px] font-bold text-[#999900] bg-[#ffffcc] px-1.5 py-0.2 rounded">
                    (UTC)
                  </span>
                </label>
                <input
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className={`w-full px-3.5 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] ${
                    validationErrors.startDate ? "border-red-400 bg-red-50/30" : "border-gray-200"
                  }`}
                  required
                />
                {formData.startDate ? (
                  <div className="mt-1.5 space-y-0.5 text-[11px] bg-gray-50 p-2 rounded-md border border-gray-100">
                    <div className="text-gray-700 font-mono">
                      📅 <strong>UTC:</strong> {formatUtcInputDisplay(formData.startDate)}
                    </div>
                    <div className="text-gray-500">
                      🌍 <strong>Local:</strong> {formatUtcInputToLocalDisplay(formData.startDate)}
                    </div>
                  </div>
                ) : (
                  <span className="text-[11px] text-gray-400 mt-1 block">
                    Pick event start in UTC timezone
                  </span>
                )}
                {validationErrors.startDate && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertCircle size={12} /> {validationErrors.startDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  End Date & Time <span className="text-red-500">*</span>
                  <span className="ml-1 text-[10px] font-bold text-[#999900] bg-[#ffffcc] px-1.5 py-0.2 rounded">
                    (UTC)
                  </span>
                </label>
                <input
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className={`w-full px-3.5 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] ${
                    validationErrors.endDate ? "border-red-400 bg-red-50/30" : "border-gray-200"
                  }`}
                  required
                />
                {formData.endDate ? (
                  <div className="mt-1.5 space-y-0.5 text-[11px] bg-gray-50 p-2 rounded-md border border-gray-100">
                    <div className="text-gray-700 font-mono">
                      🏁 <strong>UTC:</strong> {formatUtcInputDisplay(formData.endDate)}
                    </div>
                    <div className="text-gray-500">
                      🌍 <strong>Local:</strong> {formatUtcInputToLocalDisplay(formData.endDate)}
                    </div>
                  </div>
                ) : (
                  <span className="text-[11px] text-gray-400 mt-1 block">
                    Pick event end in UTC timezone
                  </span>
                )}
                {validationErrors.endDate && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertCircle size={12} /> {validationErrors.endDate}
                  </p>
                )}
              </div>

              {/* Event Duration summary */}
              {durationText && (
                <div className="sm:col-span-2 -mt-2">
                  <div className="text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1.5 rounded-lg flex items-center justify-between">
                    <span className="flex items-center gap-1.5 font-medium">
                      ⏱️ <strong>Event Duration:</strong> {durationText}
                    </span>
                    <span className="text-[11px] text-emerald-600 font-semibold">
                      (Strict UTC Timeline)
                    </span>
                  </div>
                </div>
              )}

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe the event goals, rules, and rewards..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] resize-none"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Prize Pool Configuration */}
          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FiAward className="text-amber-500" /> Prize Pool Tiers ({prizes.length})
                </h4>
                <p className="text-xs text-gray-400">
                  Configure ZX rewards and optional dynamic coin rewards (USDT, BTC, ETH, SOL, BNB)
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddPrizeTier}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#ffff00] hover:bg-[#e6e600] text-black text-xs font-bold rounded-lg transition shadow-sm"
              >
                <FiPlus size={14} /> Add Tier
              </button>
            </div>

            {prizes.length === 0 ? (
              <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6 text-center text-xs text-gray-400 flex flex-col items-center justify-center gap-3">
                <p>No prize tiers configured. Click below to add rank rewards.</p>
                <button
                  type="button"
                  onClick={handleAddPrizeTier}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg transition shadow-sm"
                >
                  <FiPlus size={14} /> Add First Prize Tier
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {prizes.map((prize, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50/80 border border-gray-200/80 rounded-xl p-4 transition hover:border-yellow-300"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                      {/* Rank Range */}
                      <div className="md:col-span-3">
                        <label className="text-[11px] text-gray-500 uppercase font-semibold block mb-1">
                          Rank Range
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. 1 or 2-5"
                          value={prize.rankRange}
                          onChange={(e) => handlePrizeChange(idx, "rankRange", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#ffff00] font-medium"
                        />
                      </div>

                      {/* ZX Reward */}
                      <div className="md:col-span-3">
                        <label className="text-[11px] text-gray-500 uppercase font-semibold block mb-1">
                          ZX Reward
                        </label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-600">
                            ZX
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            placeholder="0"
                            value={prize.rewardZx}
                            onChange={(e) =>
                              handlePrizeChange(
                                idx,
                                "rewardZx",
                                e.target.value === "" ? "" : Number(e.target.value)
                              )
                            }
                            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#ffff00] font-mono"
                          />
                        </div>
                      </div>

                      {/* Add Coin Reward Toggle */}
                      <div className="md:col-span-5 flex flex-col justify-center">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] text-gray-500 uppercase font-semibold block">
                            Coin Reward?
                          </label>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={Boolean(prize.hasCoinReward)}
                              onChange={(e) =>
                                handlePrizeChange(idx, "hasCoinReward", e.target.checked)
                              }
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                            <span className="ml-2 text-xs font-semibold text-gray-700 select-none">
                              {prize.hasCoinReward ? (
                                <span className="text-emerald-600">Enabled</span>
                              ) : (
                                <span className="text-gray-400">None</span>
                              )}
                            </span>
                          </label>
                        </div>

                        {/* Coin Inputs if toggle is ON */}
                        {prize.hasCoinReward ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
                            {/* Coin Symbol String Input */}
                            <div>
                              <input
                                type="text"
                                placeholder="Symbol (e.g. USDT)"
                                value={prize.coinSymbol || ""}
                                onChange={(e) =>
                                  handlePrizeChange(idx, "coinSymbol", e.target.value)
                                }
                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#ffff00] font-bold text-gray-800 uppercase"
                              />
                            </div>

                            {/* Coin Amount */}
                            <div className="relative">
                              <input
                                type="number"
                                min="0"
                                step="any"
                                placeholder="Amount (e.g. 100)"
                                value={prize.coinAmount}
                                onChange={(e) =>
                                  handlePrizeChange(
                                    idx,
                                    "coinAmount",
                                    e.target.value === "" ? "" : Number(e.target.value)
                                  )
                                }
                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#ffff00] font-mono text-emerald-700 font-semibold"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="text-[11px] text-gray-400 italic mt-2">
                            Only ZX token reward configured for this rank tier.
                          </div>
                        )}
                      </div>

                      {/* Remove Button */}
                      <div className="md:col-span-1 flex justify-end md:justify-center">
                        <button
                          type="button"
                          onClick={() => handleRemovePrizeTier(idx)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Remove Tier"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Prize Pool Summary Card */}
                <div className="bg-[#ffffcc]/40 border border-[#ffff00]/60 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 mt-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#999900] animate-pulse" />
                    <span className="text-xs font-bold text-gray-800">
                      Prize Pool Total Rewards:
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
                    <span className="text-purple-700 bg-purple-50 px-2.5 py-1 rounded-lg border border-purple-200 flex items-center gap-1">
                      <FiZap size={13} /> {totalZx.toLocaleString()} ZX
                    </span>
                    {Object.keys(coinTotals).length === 0 ? (
                      <span className="text-gray-500 bg-gray-100 px-2.5 py-1 rounded-lg border border-gray-200">
                        No additional coins
                      </span>
                    ) : (
                      Object.entries(coinTotals).map(([symbol, amt]) => (
                        <span
                          key={symbol}
                          className="text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 flex items-center gap-1 font-mono"
                        >
                          🪙 {amt.toLocaleString()} {symbol}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* + Add Tier Button on Bottom Right */}
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={handleAddPrizeTier}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-xs font-bold rounded-lg transition shadow-sm"
                  >
                    <FiPlus size={14} /> Add Tier
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Terms & Conditions */}
          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FiFileText className="text-blue-500" /> Terms & Conditions ({terms.length})
                </h4>
                <p className="text-xs text-gray-400">Dynamic guidelines shown to participants</p>
              </div>
            </div>

            {/* Quick preset suggestions */}
            <div className="mb-3">
              <span className="text-[11px] font-medium text-gray-400 block mb-1.5">
                Quick Add Suggestions:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {DEFAULT_TERMS_PRESETS.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleAddPresetTerm(preset)}
                    className="text-[11px] bg-gray-100 hover:bg-gray-200 text-gray-600 px-2.5 py-1 rounded-md transition text-left truncate max-w-xs"
                    title={preset}
                  >
                    + {preset.slice(0, 45)}...
                  </button>
                ))}
              </div>
            </div>

            {/* Input to add term */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="Enter custom rule or policy..."
                value={newTermInput}
                onChange={(e) => setNewTermInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTerm();
                  }
                }}
                className="flex-1 px-3.5 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
              />
              <button
                type="button"
                onClick={handleAddTerm}
                className="px-4 py-2 bg-gray-800 hover:bg-black text-white text-xs font-medium rounded-lg transition flex items-center gap-1"
              >
                <FiPlus size={14} /> Add Rule
              </button>
            </div>

            {/* Terms List */}
            {terms.length === 0 ? (
              <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4 text-center text-xs text-gray-400">
                No terms added.
              </div>
            ) : (
              <div className="space-y-2">
                {terms.map((term, idx) => (
                  <div
                    key={idx}
                    className="flex items-start justify-between gap-3 bg-gray-50 p-2.5 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-start gap-2 text-xs text-gray-700">
                      <span className="font-semibold text-gray-400 mt-0.5">{idx + 1}.</span>
                      <span>{term}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveTerm(idx)}
                      className="text-red-400 hover:text-red-600 p-1 transition"
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2.5 text-sm font-bold text-black bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-50 rounded-xl transition shadow-sm flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Create Event"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventFormModal;
